import React, { useEffect, useRef, useState } from 'react';
import { Card, Segment } from 'tea-component';
import EChart from '../../components/EChart';
import { handleMentionData, supplementSingleData } from '@/pages/opinion/utils';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { timeLineTypeMap, timelineTypeOptions } from '../../../constants';
import { channelInfo } from '@/pages/opinion/components/ChannelLabel';
import {
  percentageFormat,
  reviewsFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';

const valueFormatMap: any = {
  mentions: reviewsFormat,
  negative: reviewsFormat,
  'non-negative': percentageFormat,
  sentiment: sentimentFormat,
};

let cancel: (() => void) | null = null;
export default function CommonEChart({
  filter,
  dealwithName = (name) => name,
  languageType,
  getServerDataFunc,
  byChannel,
  lineLegendFormatter = (name: string, splitText?: string) => name,
  Sentiment_Rating,
}: {
  filter: any;
  dealwithName?: (value: string) => string;
  languageType?: string;
  getServerDataFunc: (...param: any) => Promise<any>;
  byChannel?: boolean;
  lineLegendFormatter?: (value: string, splitText?: string) => string;
  Sentiment_Rating?: boolean;
}) {
  const [timelineType, setTimelineType] = useState('mentions');
  const [xAxisData, setXAxisData] = useState<Array<string>>([]);
  const [seriesDataArray, setSeriesDataArray] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const splitText = useRef('#_to_#');
  const { formatOptionsMessage, formatMessage } = useLanguage();

  useEffect(() => {
    if (!filter) return;
    if (loading) {
      cancel?.();
      cancel = null;
    }
    setLoading(true);
    setApiError(false);
    getServerDataFunc(
      {
        ...filter,
        timeline_type: Sentiment_Rating ? 'mentions' : timelineType,
      },
      (cancelToken: any) => {
        cancel = cancelToken;
      },
    )
      .then((resultData: any) => {
        const dateType = filter.date_type || 'daily';
        let {
          timeline_of_start_time,
          timeline_of_end_time,
          timeline_mentions,
          timeline_float,
          sorts,
          hashtag_channel_split,
        } = resultData || {};
        let data = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          (timeline_mentions || timeline_float)?.map((item: any) => ({
            ...item,
            count: item.count || item.value,
          })),
          dateType,
        );
        const needTime = ['daily', 'weekly'].includes(dateType)
          ? 'days'
          : dateType === 'hourly'
          ? 'hourly'
          : 'year';
        let { newArr, timeArr } = handleMentionData(data, needTime);
        newArr?.sort((a, b) => sorts?.indexOf(a.name) - sorts?.indexOf(b.name));
        newArr = newArr?.map((item) => ({
          ...item,
          name: dealwithName(item.name),
          key: item.name,
        }));
        if (byChannel) {
          splitText.current = hashtag_channel_split;
        }
        setXAxisData(timeArr);
        setSeriesDataArray(newArr);
      })
      .catch(() => {
        setXAxisData([]);
        setSeriesDataArray([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [timelineType, filter, byChannel]);

  useEffect(() => {
    let newSeriesDataArray = seriesDataArray.map((item) => ({
      ...item,
      name: dealwithName(item.name),
    }));
    setSeriesDataArray(newSeriesDataArray);
  }, [languageType]);

  return (
    <Card className="e-chart-wrapper">
      <Card.Body>
        {!Sentiment_Rating && (
          <Segment
            value={timelineType}
            onChange={(value) => {
              !loading && setTimelineType(value);
            }}
            style={{ marginTop: -3, marginBottom: 18 }}
            className="segment-box"
            options={formatOptionsMessage(timelineTypeOptions)}
          />
        )}
        <EChart
          dateType={filter?.date_type || 'daily'}
          loading={loading}
          tooltipFormatterFunc={(param: any, timelineType: string) => {
            const letTitle = listTimeTitleFunc(
              param[0]?.axisValue,
              filter.date_type,
            );
            return formatChartParamsDate({
              params: param,
              title: letTitle,
              textShowFun: (marker, listTitle, element) => {
                if (!byChannel) {
                  return `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;${listTitle}`;
                }
                const [channel_name, seriesName] = listTitle.split(
                  splitText.current,
                );
                const channel = channelInfo(channel_name);
                const img = channel.img;
                return `<span class="chart-common-formatter-marker">${marker}</span><img style="vertical-align: middle" src="${img}"/>&nbsp;#${seriesName}`;
              },
              valueFormat(value) {
                return valueFormatMap[timelineType](
                  timelineType === 'non-negative' ? value * 100 : value,
                );
              },
              rightTitle: formatMessage({ id: timeLineTypeMap[timelineType] }),
            });
          }}
          xAxisData={xAxisData}
          apiError={apiError}
          lineLegendFormatter={(name: string) =>
            lineLegendFormatter(name, splitText.current)
          }
          timelineType={timelineType}
          seriesDataArray={seriesDataArray}
        ></EChart>
      </Card.Body>
    </Card>
  );
}
